import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactSchema, type ContactFormData } from '@/lib/validators';
import { useContact, useContacts } from '@/composables/crm/useContacts';
import { useCompanies } from '@/composables/crm/useCompanies';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { CompanyQuickCreateDialog } from '@/components/crm/CompanyQuickCreateDialog';
import { Plus } from 'lucide-react';

export function ContactCreatePage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { createContact, updateContact } = useContacts();
  const { contact, isLoading } = useContact(id || '');
  const { companies } = useCompanies();
  const [showAddCompany, setShowAddCompany] = useState(false);

  const form = useForm<ContactFormData>({ resolver: zodResolver(contactSchema), defaultValues: { firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', department: '', description: '', companyId: '' } });

  useEffect(() => {
    if (contact && isEdit) {
      form.reset({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        jobTitle: contact.jobTitle || '',
        department: contact.department || '',
        description: contact.description || '',
        linkedInUrl: contact.linkedInUrl || '',
        dateOfBirth: contact.dateOfBirth || '',
        address: contact.address || '',
        city: contact.city || '',
        state: contact.state || '',
        country: contact.country || '',
        postalCode: contact.postalCode || '',
        companyId: contact.companyId || '',
      });
    }
  }, [contact, isEdit, form]);

  const onSubmit = (data: ContactFormData) => {
    if (isEdit) {
      updateContact.mutate({ id, data }, { onSuccess: () => navigate(`/contacts/${id}`) });
    } else {
      createContact.mutate(data, { onSuccess: () => navigate('/contacts') });
    }
  };

  if (isEdit && isLoading) return <LoadingSpinner />;

  const isPending = isEdit ? updateContact.isPending : createContact.isPending;

  return (
    <PageContainer backTo title={isEdit ? 'Edit Contact' : 'Create Contact'}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Basic Information</CardTitle></CardHeader><CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4"><FormField form={form} name="firstName" label="First Name" required /><FormField form={form} name="lastName" label="Last Name" required /></div>
            <FormField form={form} name="email" label="Email" type="email" /><FormField form={form} name="phone" label="Phone" /><FormField form={form} name="mobile" label="Mobile" /><FormField form={form} name="jobTitle" label="Job Title" /><FormField form={form} name="department" label="Department" />
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <FormSelect
                    form={form}
                    name="companyId"
                    label="Company"
                    placeholder={companies.length === 0 ? 'No companies yet' : 'Select a company'}
                    options={companies.map((c) => ({ label: c.name, value: c.id }))}
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddCompany(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" />Add
                </Button>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Additional</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormField form={form} name="linkedInUrl" label="LinkedIn URL" /><FormField form={form} name="dateOfBirth" label="Date of Birth" type="date" /><FormTextarea form={form} name="description" label="Description" />
            <FormField form={form} name="address" label="Address" /><div className="grid grid-cols-2 gap-4"><FormField form={form} name="city" label="City" /><FormField form={form} name="state" label="State" /></div><div className="grid grid-cols-2 gap-4"><FormField form={form} name="country" label="Country" /><FormField form={form} name="postalCode" label="Postal Code" /></div>
          </CardContent></Card>
        </div>
        <div className="mt-6 flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button><Button type="submit" disabled={isPending}>{isPending ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Contact')}</Button></div>
      </form>

      <CompanyQuickCreateDialog
        open={showAddCompany}
        onOpenChange={setShowAddCompany}
        onCreated={(company) => form.setValue('companyId', company.id, { shouldDirty: true })}
      />
    </PageContainer>
  );
}
